import {
  Action,
  PlaybackOptions,
  PlaybackStatistics,
  Player,
  RequestType,
  SeekRangeInfo,
  StreamTimelineEvent,
  StreamTimelineMetadata,
  ThumbnailPreviewMetadata,
  TrackInfo,
  TrackVariantInfo,
  TrackType,
  PlayerContext,
  AdPlaybackPolicy,
  PLAYBACK_STATES,
  AdComposedPlayer,
  AdBreakInfo,
  AdEventListener,
  AdTechnology,
  AdEvent,
} from '@quickplay/player-interface';
import {
  AnyFunc,
  eventObject,
  Error,
  Logger,
  createLogger,
  LoggerLevel,
  EventEmitter,
  createEventEmitter,
} from '@quickplay/foundation';
import { PALConfiguration } from '@quickplay/pal';
import { MediatailorPlayer } from '../../model/MediatailorPlayer';
import { MediatailorAdHandler } from '../../model/MediatailorAdHandler';
import { DatazoomFactory } from './DatazoomFactory';
import { DatazoomAdMetaData, OmidAccessMode } from '../../model/DatazoomAdMetaData';
import { DatazoomMediatailorAdHandlerImpl } from './DatazoomMediatailorAdHandler';
import { StreamStartUpTimeData } from '../../model/StreamStartUpTimeData';
import {

  AdFrequencyControlConfiguration,
  AFCPlaybackSession,
  createAFCPlaybackSessionManager,
} from '@quickplay/player';

/**
 * DataZoom MediaTailor Player (Decorator Pattern)
 *
 * Wraps the base MediaTailor player and attaches DataZoom collector context
 * for client-side analytics tracking.
 *
 * Responsibilities:
 * - Delegate all player methods to the base player
 * - Forward playback events to Datazoom collector
 * - Forward ad events to Datazoom ad handler
 * - Maintain MediaTailor session metadata
 */
export class DatazoomMediatailorPlayer implements MediatailorPlayer {
  private readonly player: Player;
  private readonly collectorContext: any;
  private readonly mediatailorMetadata?: DatazoomAdMetaData;
  private readonly playerContext_: PlayerContext = PlayerContext.MAIN;
  private readonly eventEmitter: EventEmitter = createEventEmitter();
  private readonly logger?: Logger;
  private readonly adHandler: MediatailorAdHandler | undefined;
  private readonly FALLBACK_TIMEOUT_IN_MS = 500; // safety delay to emit aderror
  private readonly session: any;
  private readonly streamStartUpData?: StreamStartUpTimeData;
  private readonly adFrequencySession?: AFCPlaybackSession;

  constructor(
    player: Player,
    datazoomSession: any,
    mediatailorMetadata?: DatazoomAdMetaData,
    adPlaybackPolicy?: AdPlaybackPolicy,
    streamStartUpTimeData?: StreamStartUpTimeData,
    logger?: Logger,
    videoElement?: HTMLVideoElement,
    adFrequencyControl?: AdFrequencyControlConfiguration,
  ) {
    this.player = player;
    this.mediatailorMetadata = mediatailorMetadata;

    this.logger = logger ?? createLogger(LoggerLevel.OFF);
    this.session = datazoomSession;
    this.streamStartUpData = streamStartUpTimeData;

    if (this.mediatailorMetadata?.isFallback) {
      setTimeout(() => {
        this.eventEmitter.publish(AdEvent.ADVERT_ERROR, this.mediatailorMetadata?.fallbackReason);
      }, this.FALLBACK_TIMEOUT_IN_MS);
    } else {
      this.collectorContext = DatazoomFactory.createCollectorContext(player);
      this.collectorContext.attachMediaTailorSession(this.session);

      this.collectorContext.startOmidSessionClient({
        omidServiceWindow: globalThis,
        videoElement,
        contentURL: globalThis.location?.href,
        accessModeHandler: (_url: string, _vendor: string, _params: any) => {
          // OMID requires the publisher to explicitly return an access mode;
          // vendors usually don’t provide it, so we default to 'full'.
          return _params?.accessMode || OmidAccessMode.FULL;
        },
      });
    }

    this.logger.log(
      'DatazoomMediatailorPlayer initialized with session :: ',
      datazoomSession,
      'metadata ::',
      mediatailorMetadata,
      'adPlaybackPolicy ::',
      adPlaybackPolicy,
      'SDK ::',
      DatazoomFactory.getSDK(),
    );
    this.adHandler = new DatazoomMediatailorAdHandlerImpl(
      this.player,
      {
        adMetadata: mediatailorMetadata?.adMetadata,
        adPlaybackPolicy: adPlaybackPolicy,
        session: this.session,
      },
      this.logger,
    );
    this.adFrequencySession = adFrequencyControl
      ? createAFCPlaybackSessionManager({
        player: this,
        adFrequencyControlConfiguration: adFrequencyControl,
        logger: this.logger,
      })
      : undefined;
  }
  adClicked(adUrl: string): void {
    this.adHandler?.adClicked(adUrl);
  }
  getAllAdBreaks(): AdBreakInfo[] {
    return this.adHandler?.getAllAdBreaks() || [];
  }
  adEventListener?: AdEventListener;
  adInsertionType?: AdTechnology;

  initialize(_initData: PALConfiguration): Promise<string> {
    return Promise.resolve('');
  }

  sendAdClick(): void {
    // no-op
  }

  sendAdTouch(_clickOrTouchEvent: any): void {
    // no-op
  }

  sendPlaybackStart(): void {
    // no-op
  }

  sendPlaybackEnd(): void {
    // no-op
  }

  shutdown(): void {
    // Cleanup collector context if needed
  }

  unloadAndSavePreload(): Promise<void> {
    return Promise.resolve();
  }
  restoreFromPreload(): void {
    // no-op
  }

  // Player interface - delegate to base player
  subscribe(eventName: string | eventObject, callback?: AnyFunc): void {
    this.adHandler?.subscribe(eventName, callback);
    this.player.subscribe(eventName, callback);
    this.eventEmitter.subscribe(eventName, callback);
  }

  unsubscribe(eventName: string | eventObject, callback?: AnyFunc): void {
    this.adHandler?.unsubscribe(eventName, callback);
    this.player.unsubscribe(eventName, callback);
    this.eventEmitter.unsubscribe(eventName, callback);
  }
  get adSessionId(): string | undefined {
    return this.mediatailorMetadata?.adSessionId;
  }

  get playerContext(): PlayerContext {
    if (this.playerContext_ !== PlayerContext.AD && this.player && this.instanceOfAdComposedPlayer(this.player)) {
      return this.player.playerContext;
    }

    return this.adHandler?.playerContext() || PlayerContext.MAIN;
  }
  get playerState(): string {
    if (!this.player) {
      return PLAYBACK_STATES.IDLE;
    }
    return this.player.playerState;
  }

  get playbackRate(): number {
    if (!this.player) {
      return 0;
    }
    return this.player.playbackRate;
  }

  set playbackRate(rate) {
    if (!this.player) {
      return;
    }
    this.player.playbackRate = rate;
  }

  get availableBitrates(): number[] {
    return this.player?.availableBitrates || [];
  }

  set minBitrate(minBitrate) {
    if (!this.player) {
      return;
    }
    this.player.minBitrate = minBitrate;
  }

  get minBitrate(): number | undefined {
    return this.player?.minBitrate;
  }

  set maxBitrate(maxBitrate) {
    if (!this.player) {
      return;
    }
    this.player.maxBitrate = maxBitrate;
  }

  get maxBitrate(): number | undefined {
    return this.player?.maxBitrate;
  }

  get internalPlayerName(): string {
    return this.player?.internalPlayerName || '';
  }

  get internalPlayerVersion(): string {
    return this.player?.internalPlayerVersion || '';
  }

  get rawPlayer(): any {
    return this.player?.rawPlayer;
  }

  get rawLibrary(): any {
    return this.player?.rawLibrary;
  }

  get isBuffering(): boolean {
    return this.player?.isBuffering || false;
  }

  get isSeeking(): boolean {
    return this.player?.isSeeking || false;
  }

  get currentTime(): number {
    return this.adHandler?.currentTime() ?? this.player?.currentTime ?? 0;
  }

  get currentEpochTime(): number {
    return this.player?.currentEpochTime || 0;
  }

  get duration(): number {
    return this.adHandler?.duration() ?? this.player?.duration ?? 0;
  }
  get playbackStatistics(): PlaybackStatistics | undefined {
    if (!this.player?.playbackStatistics) {
      return undefined;
    }

    return {
      ...this.player.playbackStatistics,
      ssaiStreamUrlFetchTimeInMS: this.streamStartUpData?.ssaiStreamUrlFetchTimeInMS,
      preRollAdLoadTimeMs: this.adHandler?.getPrerollLoadTimeInMS(),
    };
  }

  get isLive(): boolean {
    return this.player?.isLive || false;
  }

  get remainingTime(): number {
    return 0;
  }

  get isMute(): boolean | undefined {
    return this.player.isMute;
  }

  get playbackOptions(): PlaybackOptions | undefined {
    return this.player.playbackOptions;
  }

  get volume(): number {
    return this.player.volume || 0;
  }

  set volume(volumeLevel: number) {
    this.player.volume = volumeLevel;
  }

  load(): void {
    this.player.load();
  }

  setRequestInterceptor(requestInterceptor: (requestType: RequestType, request: any) => void): void {
    this.player.setRequestInterceptor(requestInterceptor);
  }

  setResponseInterceptor(responseInterceptor: (requestType: RequestType, response: any) => void): void {
    this.player.setResponseInterceptor(responseInterceptor);
  }

  accessFunctionHandler(json: string): void {
    this.player.accessFunctionHandler(json);
  }

  stop(): Promise<void> {
    this.adFrequencySession?.dispose();
    if (!this.player) {
      return Promise.resolve();
    }
    this.collectorContext?.stopOmidSessionClient?.();
    this.collectorContext?.detachMediaTailorSession();
    this.collectorContext?.destroy();
    this.session?.destroy();
    return this.player.stop();
  }

  play(): void {
    this.player.play();
  }

  pause(): void {
    this.player.pause();
  }

  abort(error: Error): void {
    if (!this.player) {
      return;
    }
    this.collectorContext?.stopOmidSessionClient?.();
    this.collectorContext?.detachMediaTailorSession();
    this.collectorContext?.destroy();
    this.session?.destroy();

    return this.player.abort(error);
  }

  send(eventName: StreamTimelineEvent, action: Action, metadata?: StreamTimelineMetadata): void {
    this.player.send(eventName, action, metadata);
  }

  seek(seekPoint: number): void {
    if (this.player?.isLive) {
      this.player.seek(seekPoint);
    } else {
      // Delegate VOD seeks to ad handler to respect ad playback policy
      this.adHandler?.seek?.(seekPoint);
    }
  }

  getThumbnail(position: number): Promise<ThumbnailPreviewMetadata | undefined> {
    if (!this.player) {
      return Promise.resolve(undefined);
    }
    return this.player.getThumbnail(position);
  }

  seekToLiveEdge(): void {
    return this.player.seekToLiveEdge();
  }

  seekableRange(): SeekRangeInfo {
    if (this.player.isLive) {
      return this.player.seekableRange();
    }

    return {
      start: 0,
      end: this.duration,
    };
  }

  currentOffsetFromLiveEdge(): number {
    return this.player.currentOffsetFromLiveEdge() || 0;
  }

  getAllTracks(): TrackInfo[] {
    return this.player.getAllTracks() || [];
  }

  getActiveTracks(): TrackInfo[] {
    return this.player.getActiveTracks() || [];
  }

  getVariantTracks(): TrackVariantInfo[] {
    return this.player.getVariantTracks() || [];
  }

  getAvailableTrackVariants(trackType: TrackType): TrackVariantInfo[] {
    return this.player.getAvailableTrackVariants(trackType) || [];
  }

  selectTrack(track?: TrackInfo): void {
    this.player.selectTrack(track);
  }

  muteOrUnmute(): void {
    this.player.muteOrUnmute?.();
  }

  skipAd(): void {
    //no-op
  }

  resize(_width: number, _height: number, _isFullscreen: boolean): void {
    // no-op
  }

  discardAdBreak(): void {
    // no-op
  }

  getCuePoints(): number[] {
    if (this.player.isLive) {
      return [];
    }
    return this.adHandler?.getCuePointsRelativeToContentTime?.() || [];
  }

  setPlaybackOptions(playbackOptions: PlaybackOptions): void {
    this.player.setPlaybackOptions(playbackOptions);
  }

  restrictQuality(enableRestriction: boolean): void {
    this.player.restrictQuality(enableRestriction);
  }

  availableMimeTypes(trackType: TrackType): Array<string> {
    return this.player.availableMimeTypes(trackType) || [];
  }

  setPreferredMimeTypes(trackType: TrackType, mimeTypes: Array<string>): void {
    return this.player.setPreferredMimeTypes(trackType, mimeTypes);
  }
  private instanceOfAdComposedPlayer(player_: Player): player_ is AdComposedPlayer {
    return 'playerContext' in player_;
  }
}
